/**
 * register-sw.ts — Service Worker registration + lifecycle management
 *
 * Registers the compiled sw.ts as a PWA Service Worker.
 * Handles:
 * - First install (cache priming)
 * - Update detection (new SW waiting)
 * - Offline/online detection
 * - SW message passing
 *
 * Usage (main.tsx):
 *   import { registerServiceWorker } from './lib/register-sw';
 *   registerServiceWorker({
 *     onUpdate: (reg) => showUpdateBanner(reg),
 *     onOffline: () => setOffline(true),
 *     onOnline: () => setOffline(false),
 *   });
 *
 * MIGRATION_PLAN.md § 3.3.5 — Offline-First POS
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SWRegistrationCallbacks {
  /** Called when a new SW version is installed and waiting to activate */
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  /** Called when connectivity drops */
  onOffline?: () => void;
  /** Called when connectivity is restored */
  onOnline?: () => void;
  /** Called when SW is first successfully registered */
  onReady?: (registration: ServiceWorkerRegistration) => void;
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

/**
 * Register the service worker. Safe to call multiple times — subsequent calls
 * return early if already registered.
 *
 * Must be called after first render (deferred) to avoid blocking paint.
 */
export async function registerServiceWorker(
  callbacks: SWRegistrationCallbacks = {},
): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.info('[SW] Service Workers not supported in this browser');
    return null;
  }

  if (import.meta.env.DEV) {
    // Skip SW in development — HMR and SW do not play well together
    console.info('[SW] Development mode — Service Worker disabled');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      // Use the root scope so all routes are covered
      scope: '/',
      // Use the "classic" type for maximum compatibility;
      // Vite will compile sw.ts → sw.js as a separate bundle
      type: 'classic',
    });

    console.info('[SW] Registered:', registration.scope);

    callbacks.onReady?.(registration);

    // Detect when a new SW is waiting (update available)
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (
          newWorker.state === 'installed' &&
          navigator.serviceWorker.controller
        ) {
          // A new SW is installed and waiting — notify the app
          console.info('[SW] Update available — new SW waiting');
          callbacks.onUpdate?.(registration);
        }
      });
    });

    // Detect page reloads triggered by SW activation (after skipWaiting)
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      // Reload to get the fresh shell from the new SW
      window.location.reload();
    });

    return registration;
  } catch (err) {
    console.error('[SW] Registration failed:', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Online / Offline detection
// ---------------------------------------------------------------------------

/**
 * Register global online/offline event listeners.
 * Returns a cleanup function.
 */
export function listenConnectivity(callbacks: SWRegistrationCallbacks): () => void {
  const handleOnline = () => {
    console.info('[connectivity] Online');
    callbacks.onOnline?.();
  };

  const handleOffline = () => {
    console.info('[connectivity] Offline');
    callbacks.onOffline?.();
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}

// ---------------------------------------------------------------------------
// SW messaging
// ---------------------------------------------------------------------------

/**
 * Tell a waiting Service Worker to skip waiting and activate immediately.
 * Should be called when the user confirms an update prompt.
 */
export function applyUpdate(registration: ServiceWorkerRegistration): void {
  registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
}

/**
 * Get the current SW version (responds to GET_VERSION message).
 * Resolves with the version string or null on timeout/failure.
 */
export function getSWVersion(timeoutMs = 2000): Promise<string | null> {
  return new Promise((resolve) => {
    if (!navigator.serviceWorker.controller) {
      resolve(null);
      return;
    }

    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve(null), timeoutMs);

    channel.port1.onmessage = (event: MessageEvent) => {
      clearTimeout(timer);
      if (event.data?.type === 'SW_VERSION') {
        resolve(event.data.version as string);
      } else {
        resolve(null);
      }
    };

    navigator.serviceWorker.controller.postMessage(
      { type: 'GET_VERSION' },
      [channel.port2],
    );
  });
}
